// src/services/paymentService.js
/**
 * Payment service that integrates with Stripe via Vercel API routes
 */

// Stripe plans configuration
export const PAYMENT_PLANS = {
  MONTHLY: {
    id: 'pro_monthly',
    name: 'Monthly Pro',
    price: 1.99,
    currency: 'USD',
    interval: 'monthly',
    description: 'Unlimited access to Pro features, billed monthly'
  },
  ANNUAL: {
    id: 'pro_annual',
    name: 'Annual Pro',
    price: 9.99,
    currency: 'USD',
    interval: 'annual',
    description: 'Unlimited access to Pro features, billed annually (save 16%)'
  }
};

// Get the base URL for API routes
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:3000/api' 
  : '/api'; // In production, we can use relative URLs

/**
 * Cancel a subscription
 * @param {string} subscriptionId - The subscription ID to cancel
 * @returns {Promise<{success: boolean}>} Cancellation result
 */
export const cancelSubscription = async (subscriptionId) => {
  try {
    console.log('Calling cancelSubscription with:', { subscriptionId });
    
    const response = await fetch(`${API_BASE_URL}/stripe/cancel-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ subscriptionId }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error cancelling subscription:', error);
    throw new Error(error.message || 'Failed to cancel subscription');
  }
};

/**
 * Update a subscription plan
 * @param {string} subscriptionId - The current subscription ID
 * @param {string} newPlanId - The new plan ID to switch to
 * @returns {Promise<{success: boolean}>} Update result
 */
export const updateSubscription = async (subscriptionId, newPlanId) => {
  try {
    console.log('Calling updateSubscription with:', { subscriptionId, newPlanId });
    
    const response = await fetch(`${API_BASE_URL}/stripe/update-subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ subscriptionId, newPlanId }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error updating subscription:', error);
    throw new Error(error.message || 'Failed to update subscription');
  }
};

/**
 * Create a Stripe checkout session for subscription
 * @param {Object} plan - Plan details from PAYMENT_PLANS
 * @param {string} uid - User ID
 * @param {string} email - User email
 * @returns {Promise<{url: string}>} - Stripe checkout URL
 */
export const createCheckoutSession = async (plan, uid, email) => {
  try {
    console.log('Creating checkout with plan:', plan, 'for user:', uid, 'email:', email);
    
    const response = await fetch(`${API_BASE_URL}/stripe/create-checkout`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        plan, 
        userId: uid,
        email 
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Response from create-checkout:', data);
    
    if (!data || !data.url) {
      console.error('No checkout URL returned:', data);
      throw new Error('No checkout URL returned');
    }
    
    return { url: data.url };
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw new Error(error.message || 'Failed to initialize checkout session');
  }
};

/**
 * Verify the subscription status after a Stripe checkout
 * @param {string} sessionId - The Stripe checkout session ID
 * @returns {Promise<{success: boolean, subscription: Object}>} - Result object
 */
export const verifyCheckoutSession = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/stripe/verify-session?sessionId=${sessionId}`);
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error verifying checkout session:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user subscription status from Stripe
 * @param {string} uid - User ID
 * @param {string} email - User email
 * @returns {Promise<{tier: string, subscription: Object|null}>} - User subscription data
 */
export const getSubscriptionStatus = async (uid, email) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/stripe/customer?userId=${uid}&email=${encodeURIComponent(email)}`
    );
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting subscription status:', error);
    throw new Error(error.message || 'Failed to get subscription status');
  }
};

const paymentService = {
  createCheckoutSession,
  verifyCheckoutSession,
  cancelSubscription,
  updateSubscription,
  getSubscriptionStatus,
  PAYMENT_PLANS
};

export default paymentService;